/* eslint-disable no-underscore-dangle */
import React, { useState, useEffect } from 'react';
import MaterialTable from 'material-table';
import { Column } from 'material-table/types';
import { makeStyles, StylesProvider } from '@material-ui/core/styles';

import moment from 'moment';
import { withRouter, RouteComponentProps } from 'react-router-dom';

import SnackbarAlert from '../../../shared/components/SnackbarAlert';
import SpinnerOverlay from '../../../shared/components/SpinnerOverlay/SpinnerOverlay';
import SnackConfig from '../../../shared/config/snackConfig';
import { getAlarmGroup, AlarmDetails as AlarmDetailsModel } from '../../../shared/services/alarm';
import { mainStyles } from '../../../shared/styles/common';
import { createTableIcons } from '../../../shared/styles/createTableIcons';
import { generateClassName } from '../../../shared/styles/generateClassName';
import { round } from '../../../shared/utils/math';
import AlarmChart from './AlarmChart';
import TimeseriesExpression from '../Ranking/TimeseriesExpression';
import Hover from '../Ranking/Hover';

const useStyles = makeStyles({
  maxBorderBox: {
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  timeseries: {
    wordBreak: 'break-all',
  },
  unranked: {
    color: 'rgba(0, 0, 0, 0.54)',
    fontSize: '0.90rem',
  },
});

const dateTimeFormat = 'MMM DD YYYY, hh:mm:ss a';

export interface Details {
  timeseries: Record<string, string>;
  value: string;
  hover?: number;
}

export interface Group {
  id: number;
  jobId?: number;
  startTime: string;
  endTime: string;
  alarmDetails: Details[];
  isRanked?: boolean;
}

const getMinMax = (results: AlarmDetailsModel[]) => {
  if (results.length === 0) {
    return {
      min: undefined,
      max: undefined,
    };
  }
  const min = !results[0].error ? results[0].score : undefined;
  let max: number | undefined;
  for (let i = results.length - 1; i >= 0; i -= 1) {
    if (!results[i].error) {
      max = results[i].score;
      break;
    }
  }
  return { min, max };
};

const getPerc = (value: number, min: number | undefined, max: number | undefined): string | undefined => {
  if (typeof min === 'undefined' || typeof max === 'undefined') {
    return undefined;
  }
  if (min === max) {
    return '100.00%';
  }
  return `${round(((max - value) / (max - min)) * 100)}%`;
};

type AlarmDetailsProps = RouteComponentProps<Record<string, string>>;

const AlarmDetails: React.FC<AlarmDetailsProps> = ({ match }: AlarmDetailsProps) => {
  const classes = { ...mainStyles(), ...useStyles() };

  const { id } = match.params;

  const [alarmGroupLoading, setAlarmGroupLoading] = useState(true);
  const [alarmGroup, setAlarmGroup] = useState<Group>();
  const [snackConfig, setSnackConfig] = useState(new SnackConfig({}));

  useEffect(() => {
    void (async (): Promise<void> => {
      setAlarmGroupLoading(true);
      try {
        const group = await getAlarmGroup(parseInt(id, 10));
        const { min, max } = getMinMax(group.alarmDetails);
        const alarmDetails = group.alarmDetails.map(({ timeseries, score, error }) => ({
          timeseries,
          value: error ? 'FAILED' : getPerc(score!, min, max) || 'N/A',
          hover: !error ? score : undefined,
        }));
        const newGroup = { ...group, alarmDetails };
        setAlarmGroup(newGroup);
      } catch (error) {
        setSnackConfig(
          new SnackConfig({
            open: true,
            message: JSON.stringify(error),
          })
        );
      }
      setAlarmGroupLoading(false);
    })();
  }, [id]);

  const getColumns = (isRanked: boolean) => {
    const columns: Column<Details>[] = [
      {
        title: 'Timeseries',
        type: 'string',
        cellStyle: {
          width: '100%',
        },
        // eslint-disable-next-line react/display-name
        render: ({ timeseries }: Details): React.ReactNode => {
          const timeseriesClone = { ...timeseries };
          delete timeseriesClone.__name__;
          const labels = JSON.stringify(timeseriesClone);
          const name = timeseries.__name__;

          return <TimeseriesExpression name={name} labels={labels} />;
        },
      },
    ];

    if (isRanked) {
      columns.push({
        title: 'Score',
        type: 'numeric',
        // eslint-disable-next-line react/display-name
        render: ({ value, hover }: Details): React.ReactNode => <Hover value={value} hover={hover} />,
      });
    }
    return columns;
  };

  return (
    <div className={classes.container}>
      <div className={classes.maxBorderBox}>
        <SpinnerOverlay active={alarmGroupLoading}>
          <StylesProvider generateClassName={generateClassName()}>
            {alarmGroup && (
              <MaterialTable
                title={
                  alarmGroup.isRanked || alarmGroup.alarmDetails.length <= 1 ? (
                    <div>
                      Alarms from <b>{moment(alarmGroup.startTime).format(dateTimeFormat)}</b> till{' '}
                      <b>{moment(alarmGroup.endTime).format(dateTimeFormat)}</b>
                    </div>
                  ) : (
                    <div>
                      Alarms from <b>{moment(alarmGroup.startTime).format(dateTimeFormat)}</b> till{' '}
                      <b>{moment(alarmGroup.endTime).format(dateTimeFormat)}</b>{' '}
                      <span className={classes.unranked}>(Unranked)</span>
                    </div>
                  )
                }
                options={{
                  emptyRowsWhenPaging: false,
                  search: false,
                }}
                columns={getColumns(alarmGroup.isRanked || false)}
                data={alarmGroup.alarmDetails}
                icons={createTableIcons()}
                detailPanel={({ timeseries }: Details) => (
                  <AlarmChart timeseries={timeseries} start={alarmGroup.startTime} end={alarmGroup.endTime} />
                )}
                onRowClick={(event, rowData, togglePanel?) => {
                  if (togglePanel) togglePanel();
                }}
              />
            )}
          </StylesProvider>
        </SpinnerOverlay>
      </div>
      <SnackbarAlert snackConfig={snackConfig} setSnackConfig={setSnackConfig} />
    </div>
  );
};

export default withRouter(AlarmDetails);
